import { FC, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { z } from 'zod';

import {
  Button,
  Form,
  Icon,
  Link,
  RyeconInfoOutline,
  RyeconPencil,
  Sizes,
  SubmitFunction,
  Textarea,
  Tooltip,
  useZodForm,
} from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDescriptionChanges } from '@/context/DescriptionChangesProvider';
import { useResourceInfo } from '@/context/ResourceInfoProvider';
import {
  entityTypes,
  PrParams,
  usePrMutation,
} from '@/hooks/visual-editor/descriptionChanges';

export const EditDisabledReasons = {
  DBT_CREDS_ERROR:
    'Your dbt Cloud permissions do not allow developing in this project.',
  GIT_CREDENTIALS_CHECK_FAILED: 'Git credentials check failed.',
  UNSUPPORTED_GIT_PROVIDER: 'Unsupported git provider.',
  DESCRIPTION_INHERITED: 'Description is inherited.',
  LATEST_GIT_SHA_NULL:
    'No metadata found for this resource, please run a job in this environment.',
  PATCH_PATH_EMPTY:
    'No configs or properties for this resource found in project .yml files',
} as const;

const DEFAULT_ERROR_MESSAGE = 'Failed to create pull request, please try again.';

export const NO_DESCRIPTION = '_This resource does not have a description_';

export const containsDocFunctionRegex = /\{\{\s*doc\(['"].*?['"]\)\s*\}\}/;

export const CONTAINS_DOC_FUNCTION_ERROR_MESSAGE =
  "Description updates don't support resources using doc function";

// this should match the PrParams interface
const schema = z.object({
  projectId: z.number(),
  resourceIdentifiers: z.array(z.string()).nonempty(),
  filePath: z.string(),
  description: z
    .string()
    .refine((description) => !containsDocFunctionRegex.test(description), {
      message: CONTAINS_DOC_FUNCTION_ERROR_MESSAGE,
    }),
  latestGitSha: z.string(),
  entityType: z.enum(entityTypes),
});

interface DescriptionFormProps {
  currDescription: string | undefined;
  isDescriptionInherited: boolean;
  editingDescription: boolean;
  setEditingDescription: (editingDescription: boolean) => void;
  descriptionInfo: React.ReactNode | null;
  onError?: (error: string) => void;
}

export const DescriptionForm: FC<DescriptionFormProps> = ({
  currDescription,
  isDescriptionInherited,
  editingDescription,
  setEditingDescription,
  descriptionInfo,
  onError,
}) => {
  const [newPrUrl, setNewPrUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [descriptionError, setDescriptionError] = useState('');

  const { projectId } = useDbtCloudProject();
  const { filePath: filePathFromContext, latestGitSha } = useResourceInfo();
  const {
    resourceIdentifiers,
    entityType,
    pullRequest,
    featureEnabled,
    isLoading: prLoading,
    dbtCredsError,
    gitCredsError,
    unsupportedGitProvider,
  } = useDescriptionChanges();

  const { mutateAsync: createPr, isPending } = usePrMutation();

  // This is to ensure that when a model with no description is edited,
  // the description field does not show the placeholder text
  const defaultDescription = currDescription === NO_DESCRIPTION ? '' : currDescription;

  // Notify parent component when error state changes
  useEffect(() => {
    if (onError) {
      onError(descriptionError);
    }
  }, [descriptionError, onError]);

  const displayPrUrl = newPrUrl || pullRequest?.url;

  const defaultValues = useMemo(() => {
    // In the case of a source collection, the filePath is not available without the resource

    return {
      projectId,
      resourceIdentifiers,
      filePath: filePathFromContext,
      description: defaultDescription,
      latestGitSha,
      entityType,
    };
  }, [
    projectId,
    resourceIdentifiers,
    filePathFromContext,
    defaultDescription,
    latestGitSha,
    entityType,
  ]);

  // @ts-ignore
  const formMethods = useZodForm(schema, {
    defaultValues,
  });

  const { showEditButton, editEnabled, editDisabledReason } = useMemo(() => {
    const showEditButton =
      !!featureEnabled &&
      !prLoading &&
      !newPrUrl &&
      !!projectId &&
      !pullRequest &&
      !editingDescription;

    const editEnabled =
      showEditButton &&
      !dbtCredsError &&
      !gitCredsError &&
      !unsupportedGitProvider &&
      latestGitSha &&
      !(isPending || isSubmitting);

    const editDisabledReason =
      (dbtCredsError ? EditDisabledReasons.DBT_CREDS_ERROR : '') ||
      (gitCredsError ? EditDisabledReasons.GIT_CREDENTIALS_CHECK_FAILED : '') ||
      (unsupportedGitProvider ? EditDisabledReasons.UNSUPPORTED_GIT_PROVIDER : '') ||
      (!latestGitSha ? EditDisabledReasons.LATEST_GIT_SHA_NULL : '');
    return {
      showEditButton,
      editEnabled,
      editDisabledReason,
    };
  }, [
    featureEnabled,
    prLoading,
    newPrUrl,
    pullRequest,
    editingDescription,
    dbtCredsError,
    gitCredsError,
    latestGitSha,
    isPending,
    isSubmitting,
    unsupportedGitProvider,
    projectId,
  ]);

  const onSubmit: SubmitFunction = async (newPr: PrParams) => {
    setIsSubmitting(true);
    setEditingDescription(false);
    try {
      const pr = await createPr(newPr);
      setNewPrUrl(pr.url);
    } catch (error: any) {
      const errMessage = error?.response?.data?.detail ?? DEFAULT_ERROR_MESSAGE;
      toast.error(errMessage, { id: 'create-pr-error' });
      setDescriptionError(error?.response?.data?.detail ?? DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Counting lines in the description to set the height of the textarea
  const descriptionLines = (currDescription || '').split(/\r\n|\r|\n/).length + 1;

  const isSaveDisabled =
    // Description is empty, and the user has not written anything new
    (currDescription === NO_DESCRIPTION && formMethods.watch('description') === '') ||
    // Description is the same as the current description
    (formMethods.watch('description') === currDescription && !isDescriptionInherited);

  const isColumnType = entityType
    ? ['model_column', 'source_table_column'].includes(entityType)
    : false;

  if (!featureEnabled) return <>{descriptionInfo}</>;
  return (
    <Form
      formMethods={formMethods}
      defaultValues={defaultValues}
      isLoading={isPending}
      onSubmit={onSubmit}
    >
      {editingDescription ? (
        <div className={`${isColumnType ? 'mt-2' : ''}`}>
          <Textarea
            name="description"
            data-testid="edit-description-textarea"
            rows={descriptionLines}
            isEdit
          />
          <div className="mt-2 space-x-1">
            <Button
              type="secondary"
              text="Save"
              size={isColumnType ? Sizes.xs : Sizes.md}
              buttonType="submit"
              isDisabled={isSaveDisabled}
              data-testid="save-description-button"
            />
            <Button
              type="secondary"
              text="Cancel"
              size={isColumnType ? Sizes.xs : Sizes.md}
              onClick={() => {
                setEditingDescription(false);
                formMethods.resetField('description');
              }}
              data-testid="cancel-description-button"
            />
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3 pr-2">
          <div className="min-w-0 flex-1">{descriptionInfo}</div>
          <div className="flex-shrink-0 self-start pt-2">
            {showEditButton && (
              <Button
                type="icon-button"
                size={isColumnType ? Sizes.xs : Sizes.md}
                onClick={() => {
                  setEditingDescription(true);
                }}
                isDisabled={!editEnabled}
                tooltip={editDisabledReason || 'Edit Description'}
                data-testid="edit-description-button"
              >
                <Icon ryecon={RyeconPencil} />
              </Button>
            )}
          </div>
        </div>
      )}

      {featureEnabled && (pullRequest || newPrUrl) && (
        <div className="flex items-center">
          <Tooltip
            className="mt-1"
            data-testid="pending-description-tooltip"
            content={
              <div>
                {displayPrUrl && (
                  <>
                    {' '}
                    See requested changes in the PR{' '}
                    <Link to={displayPrUrl} isInternal target="_blank" rel="noreferrer">
                      here.
                    </Link>{' '}
                  </>
                )}
                Changes must be approved then applied to the project <br />
              </div>
            }
          >
            <span className="text-sm text-fgInfo">Changes requested</span>
            <Icon className="ml-1 text-fgInfo" ryecon={RyeconInfoOutline} />
          </Tooltip>
        </div>
      )}
    </Form>
  );
};
